import React from 'react';
import PhoneStep from './PhoneStep';
import CustomerStep from './CustomerStep';
import BarberStep from './BarberStep';
import ConfirmationStep from './ConfirmationStep';
import StandByStep from './StandByStep';
import KioskManifestLoader from './KioskManifestLoader';
import api from '../../services/api';
import { T } from '../../utils/theme';

const KioskApp = () => {
  const getBranchIdFromUrl = () => {
    const pathParts = window.location.pathname.split('/');
    const kioskIndex = pathParts.indexOf('kiosk');
    if (kioskIndex !== -1 && pathParts[kioskIndex + 1]) return pathParts[kioskIndex + 1];
    return new URLSearchParams(window.location.search).get('branchId') || null;
  };

  const [step, setStep] = React.useState('standby');
  const [branchId] = React.useState(getBranchIdFromUrl());
  const [customer, setCustomer] = React.useState(null);
  const [barber, setBarber] = React.useState(null);
  const [ticket, setTicket] = React.useState(null);
  const [phoneForNewCustomer, setPhoneForNewCustomer] = React.useState(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    let timer;
    const resetToStandby = () => {
      setStep('standby');
      setCustomer(null);
      setBarber(null);
      setTicket(null);
      setPhoneForNewCustomer(null);
      setError(null);
    };
    const resetTimer = () => {
      if (timer) clearTimeout(timer);
      if (step !== 'standby') timer = setTimeout(resetToStandby, 120000);
    };
    const handler = () => resetTimer();
    window.addEventListener('click', handler);
    window.addEventListener('touchstart', handler);
    resetTimer();
    return () => {
      window.removeEventListener('click', handler);
      window.removeEventListener('touchstart', handler);
      if (timer) clearTimeout(timer);
    };
  }, [step]);

  const criarTicket = async (selectedBarber) => {
    setSubmitting(true);
    setError(null);
    try {
      const body = {
        branchId: branchId || null,
        customerId: customer.id,
        serviceIds: [],
        barberId: selectedBarber === 'RANDOM' ? 'RANDOM' : (selectedBarber?.id || null),
        pago: false,
      };
      const t = await api.post('/kiosk/tickets', body);
      let position = null;
      try {
        const info = await api.get(`/kiosk/tickets/${t.id}`);
        position = info.position;
      } catch {}
      setTicket({ ...t, position });
      setStep('done');
    } catch (e) {
      setError(e?.response?.data?.message || 'Erro ao entrar na fila');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBarberSelect = (b) => {
    setBarber(b);
    criarTicket(b);
  };

  const reset = () => {
    setStep('standby');
    setCustomer(null);
    setBarber(null);
    setTicket(null);
    setPhoneForNewCustomer(null);
    setError(null);
  };

  return (
    <div style={{ width: '100vw', height: '100vh', background: T.bg, position: 'relative', overflow: 'hidden' }}>
      <KioskManifestLoader branchId={branchId} />
      <div style={{ width: '100%', height: '100%', maxWidth: 500, margin: '0 auto', position: 'relative' }}>
        {step === 'standby' && <StandByStep onStart={() => setStep('phone')} />}
        {step === 'phone' && (
          <PhoneStep branchId={branchId}
            onFound={(c) => { setCustomer(c); setStep('barber'); }}
            onNew={(phone) => { setPhoneForNewCustomer(phone); setStep('customer'); }} />
        )}
        {step === 'customer' && (
          <CustomerStep branchId={branchId} initialPhone={phoneForNewCustomer}
            onCreated={(c) => { setCustomer(c); setPhoneForNewCustomer(null); setStep('barber'); }}
            onBack={() => { setPhoneForNewCustomer(null); setStep('phone'); }} />
        )}
        {step === 'barber' && (
          <BarberStep branchId={branchId}
            onSelect={handleBarberSelect}
            onBack={() => setStep('phone')} />
        )}
        {step === 'done' && <ConfirmationStep ticket={ticket} onFinish={reset} />}

        {/* Submitting overlay */}
        {submitting && (
          <div style={{
            position: 'absolute', inset: 0, background: 'rgba(242,242,247,0.85)',
            backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 100,
          }}>
            <div style={{ textAlign: 'center' }}>
              <div className="animate-spin" style={{
                width: 40, height: 40, borderRadius: 20, margin: '0 auto 16px',
                border: `3px solid ${T.fill}`, borderTopColor: T.accent,
              }} />
              <div style={{ fontSize: 17, fontWeight: 600, color: T.label }}>Entrando na fila...</div>
            </div>
          </div>
        )}

        {/* Error toast */}
        {error && (
          <div style={{
            position: 'absolute', bottom: 40, left: 24, right: 24,
            background: 'rgba(255,59,48,0.12)', border: '1px solid rgba(255,59,48,0.2)',
            borderRadius: 14, padding: '14px 18px', zIndex: 101,
          }}>
            <div style={{ fontSize: 14, color: T.danger, textAlign: 'center' }}>{error}</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default KioskApp;
